import {
  XINGSHAN_VENDOR_ID,
  XINGSHAN_X87_PRODUCT_ID,
} from "./es87LightingUtils";

/** X87 HE 空格 3 灯珠列（与 ES68FiveSpaceUtils.X87HE_SPACE_LED_MATRIX_COLS 一致） */
export const X87HE_SPACE_LED_MATRIX_COLS = [4, 6, 8] as const;

type X87HeLightingConfig = {
  solutionRovider?: string;
  productId?: number;
  vendorId?: number;
};

/** 星闪 X87 HE（7178 / 7334）自定义灯网格 */
export function isXingshanX87HeLightingGrid(
  config?: X87HeLightingConfig
): boolean {
  if (config?.solutionRovider !== "xingshan") return false;
  return (
    Number(config.productId) === XINGSHAN_X87_PRODUCT_ID &&
    Number(config.vendorId) === XINGSHAN_VENDOR_ID
  );
}

/** SDK 自定义灯网格 → UI 展示网格（与 useKeyboardLighting 7178 分支一致） */
export function transformXingshanX87HeLightingGrid<
  T extends { R?: number; G?: number; B?: number; isCustom?: boolean },
>(data: Array<Array<T>>, config?: X87HeLightingConfig): Array<Array<T>> {
  const newData = JSON.parse(JSON.stringify(data)) as Array<Array<T>>;
  if (!isXingshanX87HeLightingGrid(config)) return newData;
  if (Array.isArray(newData[0])) {
    newData[0].splice(2, 1);
    newData[0].push({
      R: 0,
      G: 0,
      B: 0,
      isCustom: false,
    } as T);
  }
  if (Array.isArray(newData[4])) {
    newData[4].splice(1, 1);
    const spliceData = JSON.parse(JSON.stringify(newData[4][14]));
    newData[4].splice(12, 0, spliceData);
  }
  return newData;
}

/**
 * layout originalCol → 变换后展示网格列
 * - 行 0：splice(2,1) 后 col≥3 左移 1（Pause col16→15）
 * - 行 4：splice(1,1) 后 col2–13 左移 1；col14 经 insert 仍落在 14
 */
export function resolveX87HeLightingGridCol(
  matrixRow: number,
  originalCol: number
): number {
  if (matrixRow === 0 && originalCol >= 3) {
    return originalCol - 1;
  }
  if (matrixRow === 4 && originalCol > 1 && originalCol < 14) {
    return originalCol - 1;
  }
  return originalCol;
}

/**
 * 虚拟键盘选中键 → X87 HE 展示网格 (row,col)
 * 分裂空格多颗 keyCode 44 须保留 originalCol（col 4/6/8）
 */
export function resolveX87HeCustomLightGridCell(
  layoutRow: number,
  keyCode: number,
  fallbackCol: number
): { row: number; col: number } {
  if (
    keyCode === 44 &&
    layoutRow === 5 &&
    X87HE_SPACE_LED_MATRIX_COLS.includes(
      fallbackCol as (typeof X87HE_SPACE_LED_MATRIX_COLS)[number]
    )
  ) {
    return { row: layoutRow, col: fallbackCol };
  }
  return {
    row: layoutRow,
    col: resolveX87HeLightingGridCol(layoutRow, fallbackCol),
  };
}

/** 将 UI 选中的 layout 键位列表转为 X87 HE 灯光矩阵行列（SDK 写入） */
export function mapLayoutKeysToX87HeCustomLightCells(
  keys: Array<{
    layoutRow: number;
    keyCode: number;
    originalCol: number;
  }>
): Array<{ row: number; col: number }> {
  return keys.map(({ layoutRow, keyCode, originalCol }) =>
    resolveX87HeCustomLightGridCell(layoutRow, keyCode, originalCol)
  );
}
